import { Lock, Unlock, Calculator } from "lucide-react";
import AutoJobSuggestion from "@/components/review/AutoJobSuggestion";
import { confMeta } from "@/components/review/AISummaryCard";
import BiSelect from "@/components/BiSelect";
import BiDatePicker from "@/components/BiDatePicker";

function Field({ label, conf, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
          {label}
        </label>
        {conf && (
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${conf.cls}`}>
            AI · {conf.label}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Full editable field grid — supplier / total / GST / date / job / category / cost code / tag / description / notes.
 * Auto-Job suggestion chip sits directly under the Job selector.
 */
export default function EditableFieldsCard({
  form,
  setForm,
  jobs,
  activeJobId,
  supplierSuggestions,
  jobTouched,
  onApplyJob,
}) {
  const conf = form.confidence || {};
  return (
    <div className="bi-surface p-4 space-y-4">
      <Field label="Supplier" conf={confMeta(conf.supplier)}>
        <input
          data-testid="rv-supplier"
          className="w-full bi-input"
          value={form.supplier}
          onChange={(e) => setForm({ ...form, supplier: e.target.value })}
          placeholder="e.g. Bunnings Warehouse"
        />
        {supplierSuggestions.length > 0 && !form.supplier && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {supplierSuggestions.slice(0, 5).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setForm({ ...form, supplier: s })}
                className="text-[11px] px-2.5 py-1 rounded-full bi-chip hover:border-[#FF9500]/40 hover:text-white transition"
                data-testid={`rv-supplier-chip-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Total (AUD)" conf={confMeta(conf.total)}>
          <input
            data-testid="rv-total"
            type="number"
            step="0.01"
            className="w-full bi-input"
            value={form.total}
            onChange={(e) => setForm({ ...form, total: e.target.value })}
          />
        </Field>
        <Field label="GST" conf={confMeta(conf.gst)}>
          <div className="relative">
            <input
              data-testid="rv-gst"
              type="number"
              step="0.01"
              disabled={!form.gst_locked}
              className={`w-full bi-input pr-14 ${!form.gst_locked ? "opacity-80" : ""}`}
              value={form.gst}
              onChange={(e) => setForm({ ...form, gst: e.target.value })}
            />
            <button
              data-testid="rv-gst-lock"
              type="button"
              onClick={() => setForm({ ...form, gst_locked: !form.gst_locked })}
              className={`absolute right-1.5 top-1.5 h-11 w-11 rounded-lg flex items-center justify-center ${
                form.gst_locked ? "bg-[#FF9500] text-white" : "bi-ghost-btn"
              }`}
            >
              {form.gst_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
            </button>
          </div>
          <div className="text-[11px] text-white/50 mt-1 flex items-center gap-1">
            <Calculator className="w-3 h-3" />
            {form.gst_locked
              ? "Manual override — tap unlock to auto-calc"
              : `Auto 1/11th of $${Number(form.total || 0).toFixed(2)}`}
          </div>
        </Field>
      </div>

      <Field label="Date">
        <BiDatePicker
          data-testid="rv-date"
          className="w-full bi-input"
          value={form.entry_date}
          onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Job">
          <BiSelect
            data-testid="rv-job"
            className="w-full bi-input"
            value={form.job_id}
            onChange={(e) => onApplyJob(e.target.value, { auto: false, suggestion: null })}
          >
            <option value="">— Unassigned —</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.name}
              </option>
            ))}
          </BiSelect>
          <AutoJobSuggestion
            form={form}
            jobs={jobs}
            activeJobId={activeJobId}
            jobTouched={jobTouched}
            onApply={onApplyJob}
          />
        </Field>
        <Field label="Deduction category">
          <BiSelect
            data-testid="rv-category"
            className="w-full bi-input"
            value={form.deduction_category}
            onChange={(e) => setForm({ ...form, deduction_category: e.target.value })}
          >
            <option value="materials">Materials</option>
            <option value="equipment">Tools &amp; Equipment</option>
            <option value="vehicle">Fuel &amp; Vehicle</option>
            <option value="subcontractor">Subcontractor</option>
            <option value="site">Site Costs</option>
            <option value="safety">PPE &amp; Safety</option>
            <option value="admin">Admin &amp; Office</option>
            <option value="travel">Meals &amp; Travel</option>
          </BiSelect>
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Cost code (optional)">
          <input
            data-testid="rv-cost-code"
            className="w-full bi-input"
            value={form.cost_code}
            onChange={(e) => setForm({ ...form, cost_code: e.target.value })}
          />
        </Field>
        <Field label="Tag (optional)">
          <input
            data-testid="rv-cat-tag"
            className="w-full bi-input"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />
        </Field>
      </div>

      <Field label="Description">
        <input
          data-testid="rv-desc"
          className="w-full bi-input"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="What did you buy?"
        />
      </Field>

      <Field label="Notes">
        <textarea
          data-testid="rv-notes"
          rows={2}
          className="w-full bi-input"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </Field>
    </div>
  );
}
